import { useEffect } from 'react';
const ENTITY_KEY_FIELD_OVERRIDES = {};
const DEFAULT_KEY_FIELDS = ['id'];
/*
  These 2 hooks work with the instance pool of Apollo Cache and apply all incoming `update`/`delete` operations
  to the entities in the cache. This way, the UI is updated in real-time when the server sends updates.
*/
export const useDefaultUpdatesSubscription = (wsEventBus, client) => {
    useEffect(() => {
        const updateEventsSubscription = wsEventBus.onOperation('update').subscribe({
            next(value) {
                client.cache.modify({
                    id: client.cache.identify(generateCacheIdObject(value.entityName, value.attributes)),
                    fields: generateFieldModifiersMap({
                        ...value,
                        previousAttributes: {
                            ...value.previousAttributes,
                            __typename: value.entityName,
                        },
                        attributes: {
                            ...value.attributes,
                            __typename: value.entityName,
                        },
                    }),
                });
                if (value.entityName !== 'ConversationMessageDispatch' ||
                    !('conversationMessageId' in value.attributes)) {
                    return;
                }
                const parentMsgRef = client.cache.identify({
                    __typename: 'ConversationMessage',
                    id: value.attributes.conversationMessageId,
                });
                client.cache.modify({
                    id: parentMsgRef,
                    fields: {
                        messageDispatches(existingRefs = [], { toReference }) {
                            // rebuild the array (forces a new array instance)
                            // eslint-disable-next-line
                            return existingRefs.map((ref) => {
                                // @ts-expect-error temp
                                if (ref.id !== value.attributes.id) {
                                    return ref;
                                }
                                // keep exactly the same references, but
                                // by returning a fresh array, Apollo will
                                // see the field as “changed” and re-render
                                return toReference({
                                    __typename: 'ConversationMessageDispatch',
                                    // readField('id', ref) or attributes.id
                                    id: 
                                    // whichever you prefer to be bullet-proof:
                                    // eslint-disable-next-line
                                    typeof ref === 'object' && '__ref' in ref
                                        ? ref.__ref.split(':')[1]
                                        : value.attributes.id,
                                });
                            });
                        },
                    },
                });
            },
        });
        return () => {
            updateEventsSubscription.unsubscribe();
        };
    }, [wsEventBus, client]);
};
export const useDefaultDeletesSubscription = (wsEventBus, client) => {
    useEffect(() => {
        const deleteEventsSubscription = wsEventBus.onOperation('delete').subscribe({
            next(value) {
                const id = client.cache.identify(generateCacheIdObject(value.entityName, value.previousAttributes));
                client.cache.evict({
                    id,
                });
                client.cache.gc();
            },
        });
        return () => {
            deleteEventsSubscription.unsubscribe();
        };
    }, [wsEventBus, client]);
};
/**
 * Generates an object containing the identification fields for an entity in Apollo Cache.
 * The returned object will take into consideration any key field overrides in ENTITY_KEY_FIELD_OVERRIDES.
 */
function generateCacheIdObject(entityName, attributes) {
    const keyFields = extractAndValidateKeyFields(entityName, attributes);
    return prepareIdentificationObject(entityName, attributes, keyFields);
}
function extractAndValidateKeyFields(entityName, attributes) {
    const keyFields = ENTITY_KEY_FIELD_OVERRIDES[entityName] || DEFAULT_KEY_FIELDS;
    if (keyFields.some((field) => !Object.prototype.hasOwnProperty.call(attributes, field))) {
        console.warn(`Missing key field for entity ${entityName}, keyFields: ${keyFields?.join(',')}`);
    }
    return keyFields;
}
function prepareIdentificationObject(entityName, attributes, keyFields) {
    const identificationObject = {
        __typename: entityName,
    };
    keyFields.forEach((field) => {
        if (field in attributes) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore attributes object is of type EntityBase, but it actually does contain the necessary fields
            identificationObject[field] = attributes[field];
        }
    });
    return identificationObject;
}
/**
 * Map any “foreign‐key” scalar field to its corresponding nested relation name.
 * Whenever a key in this map changes in the payload, we:
 *   1) overwrite the scalar (e.g. requesterId = newValue), and
 *   2) re-bind the nested relation (e.g. requester → ConversationParticipant:newValue).
 */
const RELATION_MAP = {
    requesterId: { field: 'requester', entity: 'ConversationParticipant' },
};
/*
  This utility function generates a map of field modifiers for an entity in Apollo Cache,
  which is its way to update cached data.

  When an `update` operation is performed, we receive an event where `attributes` contains
  the updated record and `previousAttributes` contains the previous state. For every scalar
  field in `previousAttributes`, we generate a modifier that returns the new value.
  https://www.apollographql.com/docs/react/caching/cache-interaction/#parameters
*/
function generateFieldModifiersMap(data) {
    const { attributes, previousAttributes } = data;
    return Object.keys(previousAttributes).reduce((acc, key) => {
        // Check required by Apollo typings for cache modifiers.
        if (typeof acc !== 'object' || acc === null)
            return acc;
        // This could be just `attributes.hasOwnProperty(key)`, but TypeScript doesn't like it.
        if (Object.prototype.hasOwnProperty.call(attributes, key)) {
            const newValue = attributes[key];
            acc[key] = () => newValue;
            const relationField = RELATION_MAP[key];
            if (relationField) {
                acc[relationField.field] = (_oldValue, { toReference }) => {
                    if (newValue === null || newValue === undefined) {
                        return null;
                    }
                    return toReference({
                        __typename: relationField.entity,
                        id: newValue,
                    });
                };
            }
        }
        return acc;
    }, {});
}
